import { cloneDeep, isNumber } from 'lodash/fp';
import { useCallback, useEffect, useState } from 'react';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import {
  Bundle,
  SelectedProduct,
  ProductId,
} from '@upstart/harmony/lib/legos/penTypes';
import { FinanceType } from '@upstart/harmony/lib/legos/constants';
import {
  getBestSelectedBundleForProduct,
  getSelectedBundlesFromProducts,
} from '@upstart/harmony/lib/pen-utils';
import {
  BundleMap,
  ProductMap,
  getSelectedBundles,
  getSelectedProductsWithBundles,
  getSelectedProducts,
} from 'util/protectionPlans';
import { getProductsRatesAndTerms } from '../../util/pencilUtils';
import PenService from '../../services/PenService';

interface Props {
  car?: Car;
  deal?: Deal;
  dealer?: Dealer;
  pencil?: Pencil;
  selectedBundles?: Bundle[];
  selectedProducts?: SelectedProduct[];
  financeType?: FinanceType | null;
  protectionPlanProductPriceOverrides?: { [key: string]: number };
  activeFinanceTerm?: number;
  selectedMileage?: number;
  isOnePayLease: boolean;
}

const useProtectionPlans = (props: Props) => {
  const [rates, setRates] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [products, setProducts] = useState<ProductMap>({});
  const [bundles, setBundles] = useState<BundleMap>({});
  const [termOptions, setTermOptions] = useState({});
  const [minMaxDebounceInEffect, setMinMaxDebounceInEffect] = useState(false);
  const [productIdBeingEdited, setProductIdBeingEdited] = useState<
    string | undefined
  >();
  const dealId = props.deal?.id;

  const fetchRates = useCallback(async () => {
    try {
      setFetching(true);
      const res = await PenService.memoizedGetRates(
        dealId,
        props.financeType,
        props.activeFinanceTerm,
        props.selectedMileage,
      );
      setRates(res);
    } finally {
      setFetching(false);
    }
  }, [
    dealId,
    props.financeType,
    props.activeFinanceTerm,
    props.selectedMileage,
  ]);

  useEffect(() => {
    if (dealId) {
      fetchRates();
    }
  }, [dealId, fetchRates]);

  useEffect(() => {
    if (
      props.car &&
      props.dealer &&
      (props.pencil || (props.selectedBundles && props.selectedProducts)) &&
      rates
    ) {
      updateProductsRatesAndTerms();
    }
  }, [
    props.car,
    props.deal,
    props.dealer,
    props.financeType,
    props.pencil,
    rates,
    props.selectedBundles,
    props.selectedProducts,
  ]);

  const setBundleAndProducts = (
    bundleMap: BundleMap,
    productMap: ProductMap,
  ) => {
    const selectedBundleIds = getSelectedBundles(bundleMap).map(b => b.id);

    const productMapWithFinalBundlePrices = Object.keys(productMap).reduce(
      (productMapAccumulator: ProductMap, productId: string) => {
        const product = productMap[productId];
        const productBundleId =
          product.bundleId ||
          getBestSelectedBundleForProduct(
            product.bundlePrices || {},
            selectedBundleIds,
          );

        const finalPrice =
          productBundleId &&
          isNumber(product.bundlePrices?.[productBundleId]?.retailPrice)
            ? product.bundlePrices?.[productBundleId]?.retailPrice
            : product.retailPrice;

        productMapAccumulator[productId] = {
          ...product,
          finalPrice,
        };
        return productMapAccumulator;
      },
      {} as ProductMap,
    );
    setBundles(bundleMap);
    setProducts(productMapWithFinalBundlePrices);
  };

  const setProductsExternal = (productMap: ProductMap) => {
    setBundleAndProducts(bundles, productMap);
  };

  const updateProductsRatesAndTerms = () => {
    const { bundleMap, productMap, options } = getProductsRatesAndTerms(
      props.car,
      props.deal,
      props.dealer,
      props.financeType,
      props.pencil,
      rates,
      props.selectedBundles,
      props.selectedProducts,
    );
    setBundleAndProducts(bundleMap as BundleMap, productMap as ProductMap);
    setTermOptions(options);
  };

  const toggleProductSelect = (id: ProductId) => {
    const productMap = cloneDeep(products);
    if (productMap[id]) {
      productMap[id].selected = !productMap[id].selected;
    }

    const selectedProductIds = Object.values(productMap)
      .filter(p => p.selected)
      .map(p => p.id);
    const bundleMap = cloneDeep(bundles);
    const allBundles = Object.values(bundleMap);
    const selectedBundles = getSelectedBundlesFromProducts(
      selectedProductIds,
      allBundles,
    );

    allBundles.forEach(bundle => {
      bundle.selected = selectedBundles.includes(bundle);
    });

    setBundleAndProducts(bundleMap, productMap);

    return {
      bundleMap,
      productMap,
    };
  };

  const toggleInCapCostFlag = (id: ProductId, isOnePayLease: boolean) => {
    const productMap = cloneDeep(products);
    if (isOnePayLease) {
      productMap[id].inCapCostForOnePayLease = !productMap[id]
        .inCapCostForOnePayLease;
    } else {
      productMap[id].inCapCostForLease = !productMap[id].inCapCostForLease;
    }
    setProducts(productMap);
  };

  const productList = Object.values(products);
  const selectedProducts = productList.filter(p => p.selected);
  const totalPrice = selectedProducts.reduce((total, product) => {
    let relevantPrice = product.finalPrice;
    if (
      props.protectionPlanProductPriceOverrides?.[product.id] &&
      props.protectionPlanProductPriceOverrides?.[product.id] >= 0
    ) {
      relevantPrice = props.protectionPlanProductPriceOverrides?.[product.id];
    }
    return total + Number(relevantPrice);
  }, 0);

  return {
    rates,
    bundles,
    fetching,
    productList,
    totalPrice,
    termOptions,
    toggleProductSelect,
    toggleInCapCostFlag,
    products,
    setProducts: setProductsExternal,
    minMaxDebounceInEffect,
    setMinMaxDebounceInEffect,
    productIdBeingEdited,
    setProductIdBeingEdited,
    getSelectedBundles,
    getSelectedProducts(bundleMap: BundleMap, productMap: ProductMap) {
      return getSelectedProductsWithBundles(
        getSelectedProducts(productMap),
        getSelectedBundles(bundleMap),
        props.car,
        props.financeType,
      );
    },
    updateProductsRatesAndTerms,
  };
};

export default useProtectionPlans;
